/**
 * What a firm sees on the dashboard before they have a client.
 *
 * Sign-up ends at the payment and hands them straight to the portal, which on day one is four zeroes
 * and four empty columns — the product working exactly as designed and looking like nothing happened.
 * So the space the pipeline will occupy carries the three things left to do instead, and gives them
 * back once there is a client to put there.
 *
 * The first client comes FIRST, deliberately. It is what they signed up to do. The firm profile and
 * the team are real work but neither is urgent, and putting either above the client makes the tool
 * feel like admin on the day it should feel useful.
 *
 * The heading counts what is actually left rather than saying "three": an adviser who is not the
 * principal never sees the team step, and a heading that promises three items above a list of two
 * is the kind of small wrongness that makes a person distrust the rest of the screen.
 */
import React from 'react';

interface FirstRunChecklistProps {
    /** Everything is done once a firm has a client — the checklist's whole job is getting to one. */
    hasClient: boolean;
    /** Licensing answered, so an SOA can name the right entity. */
    firmProfileDone: boolean;
    /**
     * An adviser firm's second step is its licensing; a firm that gives no advice has no licensing
     * to describe, so its step is the working set-up instead — reminders, wording, documents.
     */
    isAdviserFirm: boolean;
    /** Only the principal can invite, so only they are offered the team step. */
    isPrincipal: boolean;
    onInviteClient: () => void;
    /** The other way in: sit with the client and fill the profile in together. */
    onNewInPerson: () => void;
    onOpenSettings: () => void;
    /** Hidden for good once dismissed — this is a nudge, not a permanent fixture. */
    dismissed: boolean;
    onDismiss: () => void;
}

const wrap = {
    background: '#fff',
    // Written out rather than as the `border` shorthand: the compact panel below overrides only the
    // colour, and React warns (dev overlay) when a shorthand and one of its parts are mixed and the
    // part is later removed on re-render.
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#e2e8f0',
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(15,23,42,.06)',
    marginBottom: 18,
    overflow: 'hidden',
} as const;

/** The portal's own gradient — the same one behind the avatars and the primary buttons. */
const GRADIENT = 'linear-gradient(135deg,#1a6fd4,#17b8e0)';

/*
 * One width for all three buttons.
 *
 * They sit in a column at the right-hand edge of three rows, so different widths read as a ragged
 * edge rather than as three equal choices — and the widest label ("Invite a client") is what the
 * column has to fit anyway. Fixed rather than min-width so the primary action cannot quietly grow
 * wider than the two beside it.
 */
/*
 * Each button names its DESTINATION, and no two of them share a name with anything else on the
 * screen. "Settings" was the obvious label for the firm-profile step and the wrong one: the sidebar
 * already has a Settings button, so the page carried two controls with the same accessible name —
 * ambiguous to a screen reader, and ambiguous to anything else reading the page by its labels.
 */
const actionBtn = {
    width: 132,
    textAlign: 'center',
    borderRadius: 8,
    padding: '8px 0',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
} as const;

const primaryBtn = { ...actionBtn, background: GRADIENT, color: '#fff', border: 0 } as const;
const ghostBtn = { ...actionBtn, background: '#fff', color: '#475569', border: '1px solid #cbd5e1' } as const;

function Bullet({ done, n }: { done: boolean; n: number }) {
    return (
        <span
            aria-hidden="true"
            style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                border: done ? '1.5px solid #059669' : '1.5px solid #e2e8f0',
                background: done ? '#e7f6f0' : '#fff',
                color: done ? '#059669' : '#94a3b8',
            }}
        >
            {done ? '✓' : n}
        </span>
    );
}

export function FirstRunChecklist({
    hasClient,
    firmProfileDone,
    isAdviserFirm,
    isPrincipal,
    onInviteClient,
    onNewInPerson,
    onOpenSettings,
    dismissed,
    onDismiss,
}: FirstRunChecklistProps) {
    if (dismissed) return null;

    const steps = [
        {
            key: 'client',
            done: hasClient,
            // "Add", not "Invite": there are two ways to take a client on and the heading should not
            // name only one of them. A client sitting across the desk never gets an invite email.
            title: 'Add your first client',
            detail: (
                <>
                    Invite them to fill in their own details, or{' '}
                    <button
                        type="button"
                        onClick={onNewInPerson}
                        style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: '#1a6fd4', fontWeight: 600, cursor: 'pointer' }}
                    >
                        start one in person
                    </button>{' '}
                    and do it together.
                </>
            ),
            action: (
                <button type="button" style={primaryBtn} onClick={onInviteClient}>
                    Invite a client
                </button>
            ),
        },
        {
            key: 'firm',
            done: firmProfileDone,
            title: isAdviserFirm ? 'Finish your firm profile' : 'Set up your firm profile',
            detail: isAdviserFirm ? (
                <>How your advice is provided, and the details that appear on an SOA.</>
            ) : (
                <>Client reminders, the wording of your emails, and your firm&rsquo;s documents.</>
            ),
            action: (
                <button type="button" style={ghostBtn} onClick={onOpenSettings}>
                    Firm profile →
                </button>
            ),
        },
        ...(isPrincipal
            ? [
                  {
                      key: 'team',
                      // Never "done": a firm of one has nothing to add, and ticking it would claim
                      // something untrue. It simply stops being offered once the strip collapses.
                      done: false,
                      title: 'Add your team',
                      detail: <>Only if you have one. You can do this any time.</>,
                      action: (
                          <button type="button" style={ghostBtn} onClick={onOpenSettings}>
                              Your team →
                          </button>
                      ),
                  },
              ]
            : []),
    ];

    const doneCount = steps.filter((s) => s.done).length;

    // Once there is a client, the job is done and the panel shrinks to a line they can dismiss.
    if (hasClient) {
        const left = steps.filter((s) => !s.done);
        if (!left.length) return null;
        return (
            <div style={{ ...wrap, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, background: '#f0f7ff', borderColor: '#bfdbfe', overflow: 'visible' }}>
                <Bullet done n={1} />
                <span style={{ fontSize: 13, color: '#334155' }}>
                    <strong>{doneCount} of {steps.length} done.</strong> {left.map((s) => s.title).join(' · ')}
                </span>
                <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Dismiss setup reminders"
                    style={{ marginLeft: 'auto', background: 'none', border: 0, color: '#94a3b8', fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: '0 4px' }}
                >
                    ×
                </button>
            </div>
        );
    }

    const remaining = steps.length - doneCount;

    return (
        <div style={wrap}>
            {/* The gradient carries the heading rather than the whole panel: the rows underneath are
                a list to work through, and coloured ground behind them would fight the buttons. */}
            <div style={{ background: GRADIENT, color: '#fff', padding: '16px 20px' }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>
                    You&rsquo;re set up.{' '}
                    {remaining === 1 ? 'One thing left.' : `${remaining} things left.`}
                </div>
                <p style={{ fontSize: 12.5, margin: '3px 0 0', lineHeight: 1.5, opacity: 0.92 }}>
                    This disappears once you have your first client.
                </p>
            </div>
            <div style={{ padding: '4px 20px 14px' }}>
            {steps.map((s, i) => (
                <div
                    key={s.key}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '13px 0',
                        borderTop: i === 0 ? 0 : '1px solid #f1f5f9',
                    }}
                >
                    <Bullet done={s.done} n={i + 1} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{s.title}</div>
                        <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.45 }}>{s.detail}</div>
                    </div>
                    {s.action}
                </div>
            ))}
            </div>
        </div>
    );
}
